// src/pages/StockInPage.jsx
import React, { useEffect, useMemo, useState } from "react";
import styled from "styled-components";
import { toast, ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import { FaPlus, FaTrash, FaSave, FaSyncAlt, FaCalendarAlt, FaEdit } from "react-icons/fa";
import { inventoryApi } from "../utils/inventoryApi";
import { formatStoredMoney } from "../utils/currency";
import { OFFLINE_KEYS, enqueueOffline, flushQueue } from "../utils/inventoryOfflineQueue";
import { rankAllInventoryItems } from "../utils/inventoryItemRanking";
import { isFullOfflineModeEnabled } from "../utils/fullOfflineMode";
import {
  deleteOfflineInventoryEntry,
  getOfflineInventorySummary,
  listOfflineInventoryEntries,
  listOfflineInventoryItems,
  saveOfflineInventoryItem,
  saveOfflineStockIn,
  updateOfflineInventoryEntry,
} from "../offline/inventoryStore";

const COLOR = {
  primary: "var(--app-primary)",
  bg: "#f5f7fb",
  card: "#ffffff",
  line: "#e5e7eb",
  text: "#0f172a",
  muted: "#6b7280",
  ok: "#059669",
  warn: "#b45309",
  danger: "#dc2626",
};

const createLine = () => ({
  key: `${Date.now()}-${Math.random()}`,
  item_id: "",
  qty: "",
  uom: "",
  unit_cost: "",
  note: "",
});

const BASE_OPTIONS = ["kg", "g", "ltr", "ml", "pcs"];
const OTHER_UOM_VALUE = "__other__";
const normalizeUom = (uom) => String(uom || "").trim().toLowerCase();
const buildUomOptions = (item) => {
  const set = new Set(BASE_OPTIONS);
  const base = normalizeUom(item?.base_uom);
  if (base) set.add(base);
  if (Array.isArray(item?.allowed_uoms)) {
    item.allowed_uoms.forEach((uom) => {
      const normalized = normalizeUom(uom);
      if (normalized) set.add(normalized);
    });
  }
  return Array.from(set);
};
const decimalText = (value) => {
  if (value === null || value === undefined || value === "") return "";
  return String(value);
};
const buildEditState = (entry) => ({
  id: entry.id,
  item_id: entry.item_id ? String(entry.item_id) : "",
  item_name: entry.item_name || entry.item?.name || "",
  item: entry.item || null,
  date: entry.date || today(),
  qty: decimalText(entry.qty),
  uom: entry.uom || "",
  note: entry.note || "",
  ref_no: entry.ref_no || "",
  unit_cost:
    entry.unit_cost === null || entry.unit_cost === undefined ? "" : decimalText(entry.unit_cost),
  can_delete: Boolean(entry.can_delete),
});

const StockInPage = () => {
  const fullOffline = isFullOfflineModeEnabled();
  const [userId, setUserId] = useState(null);
  const [date, setDate] = useState(today());
  const [refNo, setRefNo] = useState("");
  const [lines, setLines] = useState([createLine()]);
  const [items, setItems] = useState([]);
  const [recentItems, setRecentItems] = useState([]);
  const [stockEntries, setStockEntries] = useState([]);
  const [summary, setSummary] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [refreshing, setRefreshing] = useState(false);
  const [isConnected, setIsConnected] = useState(
    typeof navigator === "undefined" ? true : navigator.onLine
  );
  const [showAddItem, setShowAddItem] = useState(false);
  const [addItemLoading, setAddItemLoading] = useState(false);
  const [newItemName, setNewItemName] = useState("");
  const [newItemUom, setNewItemUom] = useState("kg");
  const [newItemUomCustom, setNewItemUomCustom] = useState("");
  const [newItemMinStock, setNewItemMinStock] = useState("");
  const [newItemUnitsPerCase, setNewItemUnitsPerCase] = useState("");
  const [editingEntry, setEditingEntry] = useState(null);
  const [entrySubmitting, setEntrySubmitting] = useState(false);
  const [deletingEntryId, setDeletingEntryId] = useState(null);

  useEffect(() => {
    const data = JSON.parse(localStorage.getItem("userData") || "{}")?.data;
    if (!data?.id) {
      toast.error("User not found. Please login again.");
      setLoading(false);
      return;
    }
    setUserId(data.id);
  }, []);

  useEffect(() => {
    if (!userId) return;
    loadItems();
  }, [userId, date]);

  useEffect(() => {
    if (!userId) return undefined;
    if (fullOffline) {
      setIsConnected(true);
      return undefined;
    }

    const syncQueued = async () => {
      const processed = await flushQueue(OFFLINE_KEYS.STOCK_IN, inventoryApi.stockIn);
      if (processed) {
        toast.success(`Synced ${processed} offline stock entr${processed > 1 ? "ies" : "y"}`);
        loadItems();
      }
    };

    const onOnline = () => {
      setIsConnected(true);
      syncQueued();
    };
    const onOffline = () => setIsConnected(false);

    window.addEventListener("online", onOnline);
    window.addEventListener("offline", onOffline);

    if (navigator.onLine) syncQueued();

    return () => {
      window.removeEventListener("online", onOnline);
      window.removeEventListener("offline", onOffline);
    };
  }, [userId, fullOffline]);

  const loadItems = async () => {
    try {
      setLoading(true);
      const [itemsRes, summaryRes, recentRes, stockEntriesRes] = fullOffline
        ? await Promise.all([
            listOfflineInventoryItems(userId),
            getOfflineInventorySummary(userId, date),
            listOfflineInventoryItems(userId, { recent: true }),
            listOfflineInventoryEntries(userId, "in", { date }),
          ])
        : await Promise.all([
            inventoryApi.getItems({ restaurantid: userId }),
            inventoryApi.getSummary({ restaurantid: userId, date }),
            inventoryApi.getItems({ restaurantid: userId, recent: true }),
            inventoryApi.getStockInEntries({ restaurantid: userId, date }),
          ]);
      const allItems = itemsRes?.items || [];
      setItems(allItems);
      setSummary(summaryRes?.summary || null);
      setRecentItems(rankAllInventoryItems(allItems, recentRes?.items || []));
      setStockEntries(stockEntriesRes?.entries || []);
    } catch (e) {
      toast.error(e.message || "Unable to load items");
    } finally {
      setLoading(false);
    }
  };

  const updateLine = (key, field, value) => {
    setLines((prev) => prev.map((l) => (l.key === key ? { ...l, [field]: value } : l)));
  };
  const updateLineItem = (key, itemId) => {
    const selectedItem = (items || []).find((it) => String(it.id) === String(itemId));
    setLines((prev) =>
      prev.map((line) =>
        line.key === key
          ? {
              ...line,
              item_id: itemId,
              uom: selectedItem?.base_uom || line.uom || "",
            }
          : line
      )
    );
  };
  const addLine = () => setLines((prev) => [...prev, createLine()]);
  const removeLine = (key) =>
    setLines((prev) => (prev.length === 1 ? prev : prev.filter((l) => l.key !== key)));

  const assignItemToLine = (item) => {
    setLines((prev) => {
      const clone = [...prev];
      const targetIndex = clone.findIndex((line) => !line.item_id);
      const nextLine = {
        ...(clone[targetIndex] || createLine()),
        item_id: String(item.id),
        uom: item.base_uom || "",
      };
      if (targetIndex >= 0) clone[targetIndex] = nextLine;
      else clone.push(nextLine);
      return clone;
    });
  };

  const quickSelectItem = (item) => {
    if (summary?.status === "closed") {
      toast.info("Day is closed. Reopen to edit.");
      return;
    }
    setLines((prev) => {
      const clone = [...prev];
      const targetIndex = clone.findIndex((line) => !line.item_id);
      const nextLine = {
        ...(clone[targetIndex] || createLine()),
        item_id: String(item.id),
        uom: item.base_uom || "",
      };
      if (targetIndex >= 0) clone[targetIndex] = nextLine;
      else clone.push(nextLine);
      return clone;
    });
  };

  const mergedLines = useMemo(() => {
    const valid = lines.filter((l) => l.item_id && Number(l.qty));
    const map = {};
    valid.forEach((l) => {
      const k = `${l.item_id}-${l.uom || ""}-${l.unit_cost || ""}`;
      const qty = Number(l.qty) || 0;
      if (!map[k]) map[k] = { ...l, qty };
      else map[k].qty += qty;
    });
    return Object.values(map);
  }, [lines]);

  const handleSave = async () => {
    if (!userId) return;
    if (summary?.status === "closed") {
      toast.info("Day is closed. Reopen from mobile app to edit.");
      return;
    }
    if (!mergedLines.length) {
      toast.info("Add at least one item with quantity");
      return;
    }
    const payload = {
      restaurantid: userId,
      user_id: userId,
      date,
      ref_no: refNo || undefined,
      lines: mergedLines.map((l) => ({
        item_id: l.item_id,
        qty: Number(l.qty),
        uom: l.uom || undefined,
        unit_cost: l.unit_cost ? Number(l.unit_cost) : undefined,
        note: l.note || undefined,
      })),
    };
    if (fullOffline) {
      try {
        setSaving(true);
        const response = await saveOfflineStockIn(payload);
        setSummary(response?.summary || null);
        toast.success("Stock in saved offline");
        setLines([createLine()]);
        setRefNo("");
        await loadItems();
      } catch (e) {
        toast.error(e.message || "Unable to save stock");
      } finally {
        setSaving(false);
      }
      return;
    }
    if (!isConnected) {
      await enqueueOffline(OFFLINE_KEYS.STOCK_IN, payload);
      toast.info("Saved offline. It will sync automatically once internet returns.");
      setLines([createLine()]);
      setRefNo("");
      return;
    }
    try {
      setSaving(true);
      const response = await inventoryApi.stockIn(payload);
      setSummary(response?.summary || null);
      toast.success("Stock in saved");
      setLines([createLine()]);
      setRefNo("");
      await loadItems();
    } catch (e) {
      toast.error(e.message || "Unable to save stock");
    } finally {
      setSaving(false);
    }
  };

  const resetAddItem = () => {
    setNewItemName("");
    setNewItemUom("kg");
    setNewItemUomCustom("");
    setNewItemMinStock("");
    setNewItemUnitsPerCase("");
  };

  const handleAddItem = async () => {
    const name = (newItemName || "").trim();
    const resolvedUom =
      normalizeUom(newItemUom) === OTHER_UOM_VALUE
        ? normalizeUom(newItemUomCustom)
        : normalizeUom(newItemUom);
    if (!name) {
      toast.info("Enter item name");
      return;
    }
    if (!resolvedUom) {
      toast.info("Select base unit");
      return;
    }
    if (!userId) return;
    try {
      setAddItemLoading(true);
      const payload = {
        restaurantid: userId,
        name,
        base_uom: resolvedUom,
        min_stock: Number(newItemMinStock || 0),
        units_per_case:
          resolvedUom === "pcs" && newItemUnitsPerCase
            ? Number(newItemUnitsPerCase)
            : undefined,
      };
      const res = fullOffline
        ? await saveOfflineInventoryItem(userId, payload)
        : await inventoryApi.saveItem(payload);
      const item = res?.item || res?.data || null;
      toast.success("Item saved");
      setShowAddItem(false);
      resetAddItem();
      await loadItems();
      if (item?.id) assignItemToLine(item);
    } catch (e) {
      toast.error(e.message || "Unable to save item");
    } finally {
      setAddItemLoading(false);
    }
  };

  const resolveEntryItem = (entry) =>
    (items || []).find((item) => String(item.id) === String(entry?.item_id)) || entry?.item || null;

  const openEditEntry = (entry) => {
    if (!fullOffline && !isConnected) {
      toast.info("Reconnect to edit posted entries.");
      return;
    }
    if (!entry?.can_edit) {
      toast.info(entry?.edit_block_reason || "This entry cannot be edited.");
      return;
    }
    setEditingEntry(buildEditState(entry));
  };

  const updateEditingEntry = (field, value) => {
    setEditingEntry((prev) => (prev ? { ...prev, [field]: value } : prev));
  };

  const submitEntryEdit = async () => {
    if (!userId || !editingEntry?.id) return;
    if (!fullOffline && !isConnected) {
      toast.info("Reconnect to update posted entries.");
      return;
    }

    const qtyValue = Number(editingEntry.qty);
    const nextUom = normalizeUom(editingEntry.uom);
    if (!qtyValue) {
      toast.info("Enter a valid quantity");
      return;
    }
    if (!nextUom) {
      toast.info("Select a unit");
      return;
    }

    try {
      setEntrySubmitting(true);
      const changes = {
        restaurantid: userId,
        date: editingEntry.date,
        qty: qtyValue,
        uom: nextUom,
        ref_no: editingEntry.ref_no.trim() || undefined,
        unit_cost: editingEntry.unit_cost === "" ? null : Number(editingEntry.unit_cost),
        note: editingEntry.note.trim() || undefined,
      };
      const response = fullOffline
        ? await updateOfflineInventoryEntry(userId, "in", editingEntry.id, changes)
        : await inventoryApi.updateStockInEntry(editingEntry.id, changes);
      setSummary(response?.summary || null);
      setEditingEntry(null);
      toast.success("Stock entry updated");
      await loadItems();
    } catch (e) {
      toast.error(e.message || "Unable to update stock entry");
    } finally {
      setEntrySubmitting(false);
    }
  };

  const handleDeletePostedEntry = async (entry) => {
    if (!userId || !entry?.id) return;
    if (!fullOffline && !isConnected) {
      toast.info("Reconnect to delete posted entries.");
      return;
    }
    if (!entry.can_delete) {
      toast.info(entry.edit_block_reason || "This entry cannot be deleted.");
      return;
    }
    if (!window.confirm("Delete this stock-in entry?")) return;

    try {
      setDeletingEntryId(entry.id);
      const response = fullOffline
        ? await deleteOfflineInventoryEntry(userId, "in", entry.id)
        : await inventoryApi.deleteStockInEntry({
            entryId: entry.id,
            restaurantid: userId,
          });
      setSummary(response?.summary || null);
      setEditingEntry((prev) => (prev?.id === entry.id ? null : prev));
      toast.success("Stock entry deleted");
      await loadItems();
    } catch (e) {
      toast.error(e.message || "Unable to delete stock entry");
    } finally {
      setDeletingEntryId(null);
    }
  };

  const onRefresh = async () => {
    setRefreshing(true);
    await loadItems();
    setRefreshing(false);
  };

  const totals = summary?.totals || {};
  const editingItem = resolveEntryItem(editingEntry);
  const editingUomOptions = buildUomOptions(editingItem);
  const normalizedEditingUom = normalizeUom(editingEntry?.uom);
  const hasCustomEditingUom =
    normalizedEditingUom && !editingUomOptions.includes(normalizedEditingUom);
  const editingUomValue = hasCustomEditingUom ? OTHER_UOM_VALUE : normalizedEditingUom;

  return (
    <Page>
      <ToastContainer position="top-right" autoClose={2200} />
      <Top>
        <div>
          <Title>Inventory — Stock In</Title>
          <Sub>
            Opening: {Number(totals?.opening ?? 0).toFixed(2)} • Closing:{" "}
            {Number(totals?.closing ?? 0).toFixed(2)}
          </Sub>
        </div>
        <Actions>
          <StatusPill $online={isConnected}>
            {isConnected ? "Online" : "Offline"}
          </StatusPill>
          <StatusPill $closed={summary?.status === "closed"}>
            {summary?.status === "closed" ? "Closed" : "Open"}
          </StatusPill>
          <DateInput>
            <FaCalendarAlt />
            <input
              type="date"
              max={today()}
              value={date}
              onChange={(e) => setDate(e.target.value)}
            />
          </DateInput>
          <PrimaryBtn onClick={onRefresh} disabled={refreshing}>
            <FaSyncAlt /> {refreshing ? "Refreshing..." : "Refresh Items"}
          </PrimaryBtn>
          <PrimaryBtn onClick={() => setShowAddItem(true)}>
            <FaPlus /> Add Item
          </PrimaryBtn>
        </Actions>
      </Top>

      <Card>
        <FormGrid>
          <Field>
            <Label>Reference No. (optional)</Label>
            <Input
              placeholder="e.g., PO-123"
              value={refNo}
              onChange={(e) => setRefNo(e.target.value)}
            />
          </Field>
        </FormGrid>

        {loading ? (
          <Center>Loading items…</Center>
        ) : (
          <LineList>
            {lines.map((line, idx) => {
              const options = items || [];
              const selectedItem = options.find((it) => String(it.id) === String(line.item_id));
              const uomOptions = buildUomOptions(selectedItem);
              const normalizedLineUom = normalizeUom(line.uom);
              const isCustomUom = normalizedLineUom && !uomOptions.includes(normalizedLineUom);
              const uomValue = isCustomUom ? OTHER_UOM_VALUE : normalizedLineUom;
              return (
                <LineCard key={line.key}>
                  <LineHeader>
                    <span>Line {idx + 1}</span>
                    <button
                      onClick={() => removeLine(line.key)}
                      disabled={lines.length === 1 || summary?.status === "closed"}
                    >
                      <FaTrash /> Remove
                    </button>
                  </LineHeader>
                  <LineGrid>
                    <LineField>
                      <SmallLabel>Item</SmallLabel>
                      <Select
                        value={line.item_id}
                        disabled={summary?.status === "closed"}
                        onChange={(e) => updateLineItem(line.key, e.target.value)}
                      >
                        <option value="">Select item</option>
                        {options.map((it) => (
                          <option key={it.id} value={it.id}>
                            {it.name}
                          </option>
                        ))}
                      </Select>
                    </LineField>
                    <LineField>
                      <SmallLabel>Qty</SmallLabel>
                      <Input
                        value={line.qty}
                        disabled={summary?.status === "closed"}
                        onChange={(e) =>
                          updateLine(line.key, "qty", e.target.value.replace(/[^\d.]/g, ""))
                        }
                        placeholder="0"
                        inputMode="decimal"
                        pattern="[0-9.]*"
                        maxLength={8}
                      />
                    </LineField>
                    <LineField>
                      <SmallLabel>UOM</SmallLabel>
                      <Select
                        value={uomValue}
                        disabled={summary?.status === "closed"}
                        onChange={(e) => {
                          const next = e.target.value;
                          if (next === OTHER_UOM_VALUE) {
                            if (!isCustomUom) updateLine(line.key, "uom", "");
                            return;
                          }
                          updateLine(line.key, "uom", next);
                        }}
                      >
                        <option value="">Select UOM</option>
                        {uomOptions.map((uom) => (
                          <option key={uom} value={uom}>
                            {uom.toUpperCase()}
                          </option>
                        ))}
                        <option value={OTHER_UOM_VALUE}>Other</option>
                      </Select>
                      {(uomValue === OTHER_UOM_VALUE || isCustomUom) && (
                        <Input
                          style={{ marginTop: 8 }}
                          disabled={summary?.status === "closed"}
                          value={line.uom}
                          onChange={(e) => updateLine(line.key, "uom", normalizeUom(e.target.value))}
                          placeholder="Enter custom UOM"
                        />
                      )}
                    </LineField>
                    <LineField>
                      <SmallLabel>Unit Cost</SmallLabel>
                      <Input
                        value={line.unit_cost}
                        disabled={summary?.status === "closed"}
                        onChange={(e) =>
                          updateLine(line.key, "unit_cost", e.target.value.replace(/[^\d.]/g, ""))
                        }
                        placeholder="0"
                      />
                    </LineField>
                    <LineField>
                      <SmallLabel>Note</SmallLabel>
                      <Input
                        value={line.note}
                        disabled={summary?.status === "closed"}
                        onChange={(e) => updateLine(line.key, "note", e.target.value)}
                        placeholder="optional"
                      />
                    </LineField>
                  </LineGrid>
                </LineCard>
              );
            })}
          </LineList>
        )}

        <Actions>
          <GhostBtn onClick={addLine} disabled={summary?.status === "closed"}>
            <FaPlus /> Add Line
          </GhostBtn>
          <PrimaryBtn onClick={handleSave} disabled={saving || summary?.status === "closed"}>
            <FaSave /> {saving ? "Saving..." : "Save Stock"}
          </PrimaryBtn>
        </Actions>
      </Card>

      <Card style={{ marginTop: 12 }}>
        <CardHead>
          <CardTitle>Posted Entries</CardTitle>
          <SummaryMeta>{date}</SummaryMeta>
        </CardHead>
        {loading ? (
          <Center>Loading posted entries…</Center>
        ) : stockEntries.length ? (
          <SummaryList>
            {stockEntries.map((entry) => {
              const entryItem = resolveEntryItem(entry);
              const isDeleting = deletingEntryId === entry.id;
              return (
                <PostedEntryCard key={entry.id}>
                  <PostedEntryHeader>
                    <div>
                      <SummaryName>{entry.item_name || entryItem?.name || "Stock In"}</SummaryName>
                      <SummaryMeta>
                        {entry.date} • +{entry.qty} {String(entry.uom || "").toUpperCase()}
                        {entry.unit_cost !== null && entry.unit_cost !== undefined
                          ? ` • ${formatStoredMoney(entry.unit_cost)}/${String(entry.uom || "").toUpperCase()}`
                          : ""}
                      </SummaryMeta>
                    </div>
                    <PostedEntryActions>
                      <IconBtn
                        type="button"
                        title="Edit entry"
                        onClick={() => openEditEntry(entry)}
                        disabled={!entry.can_edit || isDeleting || !isConnected}
                      >
                        <FaEdit />
                      </IconBtn>
                      <IconBtn
                        type="button"
                        title="Delete entry"
                        $danger
                        onClick={() => handleDeletePostedEntry(entry)}
                        disabled={!entry.can_delete || isDeleting || !isConnected}
                      >
                        <FaTrash />
                      </IconBtn>
                    </PostedEntryActions>
                  </PostedEntryHeader>

                  {entry.ref_no ? <SummaryMeta>Ref: {entry.ref_no}</SummaryMeta> : null}
                  {entry.note ? <SummaryMeta>{entry.note}</SummaryMeta> : null}
                  {!entry.can_edit && entry.edit_block_reason ? (
                    <LockedText>{entry.edit_block_reason}</LockedText>
                  ) : null}
                </PostedEntryCard>
              );
            })}
          </SummaryList>
        ) : (
          <Center>No posted stock-in entries for this date</Center>
        )}
      </Card>

      {recentItems.length ? (
        <Card style={{ marginTop: 12 }}>
          <CardHead>
            <CardTitle>Recent Items ({recentItems.length})</CardTitle>
            <GhostBtn onClick={loadItems}>
              <FaSyncAlt /> Refresh
            </GhostBtn>
          </CardHead>
          <ChipRow>
            {recentItems.map((item) => (
              <ChipBtn
                key={item.id}
                onClick={() => quickSelectItem(item)}
                disabled={summary?.status === "closed"}
              >
                {item.name}
              </ChipBtn>
            ))}
          </ChipRow>
        </Card>
      ) : null}

      {showAddItem ? (
        <ModalBackdrop onClick={() => { setShowAddItem(false); resetAddItem(); }}>
          <ModalCard onClick={(e) => e.stopPropagation()}>
            <ModalHeader>
              <h3>Add Inventory Item</h3>
              <button onClick={() => { setShowAddItem(false); resetAddItem(); }}>✕</button>
            </ModalHeader>
            <Field>
              <Label>Item Name</Label>
              <Input
                placeholder="e.g., Tomato"
                value={newItemName}
                onChange={(e) => setNewItemName(e.target.value)}
              />
            </Field>
            <Field>
              <Label>Base Unit</Label>
              <Select
                value={
                  BASE_OPTIONS.includes(normalizeUom(newItemUom))
                    ? normalizeUom(newItemUom)
                    : OTHER_UOM_VALUE
                }
                onChange={(e) => setNewItemUom(e.target.value)}
              >
                {BASE_OPTIONS.map((opt) => (
                  <option key={opt} value={opt}>
                    {opt.toUpperCase()}
                  </option>
                ))}
                <option value={OTHER_UOM_VALUE}>Other</option>
              </Select>
              {normalizeUom(newItemUom) === OTHER_UOM_VALUE && (
                <Input
                  style={{ marginTop: 8 }}
                  placeholder="Enter custom UOM"
                  value={newItemUomCustom}
                  onChange={(e) => setNewItemUomCustom(normalizeUom(e.target.value))}
                />
              )}
            </Field>
            <Inline>
              <Field style={{ flex: 1, marginRight: 8 }}>
                <Label>Min Stock</Label>
                <Input
                  placeholder="0"
                  value={newItemMinStock}
                  onChange={(e) =>
                    setNewItemMinStock(e.target.value.replace(/[^\d.]/g, ""))
                  }
                />
              </Field>
              {newItemUom === "pcs" ? (
                <Field style={{ flex: 1, marginLeft: 8 }}>
                  <Label>Units / Case</Label>
                  <Input
                    placeholder="e.g., 24"
                    value={newItemUnitsPerCase}
                    onChange={(e) =>
                      setNewItemUnitsPerCase(e.target.value.replace(/[^\d]/g, ""))
                    }
                  />
                </Field>
              ) : (
                <div style={{ flex: 1 }} />
              )}
            </Inline>
            <PrimaryBtn onClick={handleAddItem} disabled={addItemLoading || !newItemName.trim()}>
              <FaSave /> {addItemLoading ? "Saving..." : "Save Item"}
            </PrimaryBtn>
          </ModalCard>
        </ModalBackdrop>
      ) : null}

      {editingEntry ? (
        <ModalBackdrop onClick={() => { if (!entrySubmitting) setEditingEntry(null); }}>
          <ModalCard onClick={(e) => e.stopPropagation()}>
            <ModalHeader>
              <h3>Edit Stock Entry</h3>
              <button onClick={() => { if (!entrySubmitting) setEditingEntry(null); }}>
                ✕
              </button>
            </ModalHeader>
            <Field>
              <Label>Item</Label>
              <Input value={editingEntry.item_name || editingItem?.name || ""} disabled />
            </Field>
            <Inline>
              <Field style={{ flex: 1 }}>
                <Label>Date</Label>
                <Input
                  type="date"
                  max={today()}
                  value={editingEntry.date}
                  onChange={(e) => updateEditingEntry("date", e.target.value)}
                  disabled={entrySubmitting}
                />
              </Field>
              <Field style={{ flex: 1 }}>
                <Label>Quantity</Label>
                <Input
                  value={editingEntry.qty}
                  onChange={(e) =>
                    updateEditingEntry("qty", e.target.value.replace(/[^\d.]/g, ""))
                  }
                  placeholder="0"
                  inputMode="decimal"
                  disabled={entrySubmitting}
                />
              </Field>
            </Inline>
            <Field>
              <Label>UOM</Label>
              <Select
                value={editingUomValue}
                onChange={(e) => {
                  const next = e.target.value;
                  if (next === OTHER_UOM_VALUE) {
                    if (!hasCustomEditingUom) updateEditingEntry("uom", "");
                    return;
                  }
                  updateEditingEntry("uom", next);
                }}
                disabled={entrySubmitting}
              >
                <option value="">Select UOM</option>
                {editingUomOptions.map((uom) => (
                  <option key={uom} value={uom}>
                    {String(uom).toUpperCase()}
                  </option>
                ))}
                <option value={OTHER_UOM_VALUE}>Other</option>
              </Select>
              {(editingUomValue === OTHER_UOM_VALUE || hasCustomEditingUom) && (
                <Input
                  style={{ marginTop: 8 }}
                  value={editingEntry.uom}
                  onChange={(e) => updateEditingEntry("uom", normalizeUom(e.target.value))}
                  placeholder="Enter custom UOM"
                  disabled={entrySubmitting}
                />
              )}
            </Field>
            <Field>
              <Label>Reference No. (optional)</Label>
              <Input
                value={editingEntry.ref_no}
                onChange={(e) => updateEditingEntry("ref_no", e.target.value)}
                placeholder="e.g., PO-123"
                disabled={entrySubmitting}
              />
            </Field>
            <Inline>
              <Field style={{ flex: 1 }}>
                <Label>Unit Cost (optional)</Label>
                <Input
                  value={editingEntry.unit_cost}
                  onChange={(e) =>
                    updateEditingEntry("unit_cost", e.target.value.replace(/[^\d.]/g, ""))
                  }
                  placeholder="0"
                  disabled={entrySubmitting}
                />
              </Field>
              <Field style={{ flex: 1 }}>
                <Label>Note (optional)</Label>
                <Input
                  value={editingEntry.note}
                  onChange={(e) => updateEditingEntry("note", e.target.value)}
                  placeholder="optional"
                  disabled={entrySubmitting}
                />
              </Field>
            </Inline>
            <Inline style={{ justifyContent: "space-between", flexWrap: "wrap" }}>
              <Inline style={{ gap: 8, flexWrap: "wrap" }}>
                <GhostBtn
                  type="button"
                  onClick={() => setEditingEntry(null)}
                  disabled={entrySubmitting}
                >
                  Close
                </GhostBtn>
                {editingEntry.can_delete ? (
                  <DangerBtn
                    type="button"
                    onClick={() => handleDeletePostedEntry(editingEntry)}
                    disabled={entrySubmitting || deletingEntryId === editingEntry.id}
                  >
                    <FaTrash /> Delete
                  </DangerBtn>
                ) : null}
              </Inline>
              <PrimaryBtn type="button" onClick={submitEntryEdit} disabled={entrySubmitting}>
                <FaSave /> {entrySubmitting ? "Saving..." : "Save Changes"}
              </PrimaryBtn>
            </Inline>
          </ModalCard>
        </ModalBackdrop>
      ) : null}
    </Page>
  );
};

export default StockInPage;

const today = () => {
  const d = new Date();
  const pad = (v) => String(v).padStart(2, "0");
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
};

/* ---------------- styled ---------------- */
const Page = styled.div`
  background: ${COLOR.bg};
  min-height: 100vh;
  padding: 16px;
`;
const Top = styled.div`
  display: flex;
  justify-content: space-between;
  gap: 12px;
  flex-wrap: wrap;
  margin-bottom: 12px;
`;
const Title = styled.h1`
  margin: 0;
  font-size: 1.2rem;
  font-weight: 900;
  color: ${COLOR.text};
`;
const Sub = styled.div`
  color: ${COLOR.muted};
  font-size: 0.95rem;
`;
const Actions = styled.div`
  display: flex;
  gap: 8px;
  align-items: center;
  flex-wrap: wrap;
`;
const StatusPill = styled.div`
  display: inline-flex;
  align-items: center;
  padding: 8px 12px;
  border-radius: 999px;
  font-size: 12px;
  font-weight: 800;
  border: 1px solid
    ${({ $online, $closed }) =>
      $closed ? "#f59e0b" : $online === false ? "#c2410c" : "#0f766e"};
  color: ${({ $online, $closed }) =>
    $closed ? "#92400e" : $online === false ? "#9a3412" : "#115e59"};
  background: ${({ $online, $closed }) =>
    $closed ? "#fef3c7" : $online === false ? "#ffedd5" : "#ecfeff"};
`;
const DateInput = styled.label`
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 8px 10px;
  border: 1px solid ${COLOR.line};
  border-radius: 10px;
  background: #f9f9f9;
  color: ${COLOR.text};
  input {
    border: 0;
    background: transparent;
    outline: none;
  }
`;

const Card = styled.div`
  background: ${COLOR.card};
  border: 1px solid ${COLOR.line};
  border-radius: 16px;
  padding: 14px;
  box-shadow: 0 10px 26px rgba(0, 0, 0, 0.05);
`;
const CardHead = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: 8px;
  margin-bottom: 10px;
`;
const CardTitle = styled.div`
  font-weight: 800;
  color: ${COLOR.text};
`;
const FormGrid = styled.div`
  display: grid;
  gap: 10px;
  margin-bottom: 10px;
`;
const Field = styled.div`
  display: grid;
  gap: 6px;
`;
const Label = styled.label`
  font-weight: 800;
  color: ${COLOR.text};
`;
const Input = styled.input`
  border: 1px solid ${COLOR.line};
  border-radius: 10px;
  padding: 10px 12px;
  background: #f9f9f9;
  color: ${COLOR.text};
  outline: none;
`;
const Select = styled.select`
  border: 1px solid ${COLOR.line};
  border-radius: 10px;
  padding: 10px 12px;
  background: #f9f9f9;
  color: ${COLOR.text};
  outline: none;
`;
const PrimaryBtn = styled.button`
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 10px 12px;
  border-radius: 10px;
  border: none;
  background: ${COLOR.primary};
  color: #fff;
  font-weight: 800;
  cursor: pointer;
  &:disabled {
    opacity: 0.6;
    cursor: not-allowed;
  }
`;
const GhostBtn = styled(PrimaryBtn)`
  background: var(--app-primary-soft);
  color: ${COLOR.primary};
  border: 1px solid ${COLOR.primary};
`;
const LineList = styled.div`
  display: grid;
  gap: 10px;
  margin-top: 6px;
`;
const LineCard = styled.div`
  border: 1px solid ${COLOR.line};
  border-radius: 12px;
  padding: 10px;
  background: #f8fafc;
`;
const LineHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: 10px;
  margin-bottom: 8px;
  button {
    border: 1px solid #dc2626;
    color: #dc2626;
    background: #fff5f5;
    border-radius: 10px;
    padding: 6px 8px;
    display: inline-flex;
    align-items: center;
    gap: 6px;
    cursor: pointer;
    &:disabled {
      opacity: 0.45;
      cursor: not-allowed;
    }
  }
`;
const LineGrid = styled.div`
  display: grid;
  grid-template-columns: 2fr 1fr 1fr 1fr 2fr;
  gap: 8px;
  @media (max-width: 960px) {
    grid-template-columns: repeat(2, 1fr);
  }
  @media (max-width: 600px) {
    grid-template-columns: 1fr;
  }
`;
const LineField = styled.div`
  display: grid;
  gap: 4px;
`;
const SmallLabel = styled.div`
  font-size: 12px;
  color: ${COLOR.muted};
`;
const Center = styled.div`
  text-align: center;
  color: ${COLOR.muted};
  padding: 16px;
`;
const SummaryList = styled.div`
  display: grid;
  gap: 8px;
`;
const SummaryName = styled.div`
  font-weight: 700;
  color: ${COLOR.text};
`;
const SummaryMeta = styled.div`
  color: ${COLOR.muted};
  font-size: 13px;
`;
const PostedEntryCard = styled.div`
  border: 1px solid ${COLOR.line};
  border-radius: 12px;
  padding: 12px;
  background: #f8fafc;
  display: grid;
  gap: 6px;
`;
const PostedEntryHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  gap: 12px;
`;
const PostedEntryActions = styled.div`
  display: inline-flex;
  gap: 8px;
  flex-wrap: wrap;
`;
const IconBtn = styled.button`
  width: 36px;
  height: 36px;
  border-radius: 10px;
  border: 1px solid ${({ $danger }) => ($danger ? "#fecaca" : "var(--app-primary-soft)")};
  background: ${({ $danger }) => ($danger ? "#fff1f2" : "var(--app-primary-soft)")};
  color: ${({ $danger }) => ($danger ? COLOR.danger : COLOR.primary)};
  display: inline-flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  &:disabled {
    opacity: 0.45;
    cursor: not-allowed;
  }
`;
const LockedText = styled.div`
  color: ${COLOR.warn};
  font-size: 13px;
  font-weight: 600;
`;
const ModalBackdrop = styled.div`
  position: fixed;
  inset: 0;
  background: rgba(15, 23, 42, 0.4);
  display: flex;
  align-items: flex-end;
  justify-content: center;
  padding: 16px;
  z-index: 50;
`;
const ModalCard = styled.div`
  background: #fff;
  border-radius: 16px;
  padding: 16px;
  width: min(520px, 100%);
  max-height: calc(100vh - 32px);
  overflow: auto;
  box-shadow: 0 20px 40px rgba(0, 0, 0, 0.2);
  display: grid;
  gap: 12px;
`;
const ModalHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  h3 {
    margin: 0;
  }
  button {
    border: none;
    background: transparent;
    font-size: 18px;
    cursor: pointer;
  }
`;
const ChipRow = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
`;
const ChipBtn = styled.button`
  padding: 8px 12px;
  border-radius: 999px;
  border: 1px solid ${COLOR.primary};
  background: ${(p) => (p.$active ? COLOR.primary : "#fff")};
  color: ${(p) => (p.$active ? "#fff" : COLOR.primary)};
  font-weight: 700;
  cursor: pointer;
  &:disabled {
    opacity: 0.45;
    cursor: not-allowed;
  }
`;
const Inline = styled.div`
  display: flex;
  gap: 8px;
  align-items: flex-start;
`;
const DangerBtn = styled(PrimaryBtn)`
  background: ${COLOR.danger};
  color: #fff;
`;
